/**
 * WeeklyTipsCard Component
 * Displays a random helpful tip from the knowledge base
 * Light, friendly information to keep the app feeling supportive
 */

import React, { useState, useEffect } from 'react';
import Card from '../common/Card';
import { FaLightbulb } from 'react-icons/fa';
import dashboardService from '../../services/dashboardService';

const WeeklyTipsCard = () => {
  const [tip, setTip] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTip = async () => {
      setLoading(true);
      try {
        const fetchedTip = await dashboardService.fetchWeeklyTip();
        setTip(fetchedTip);
      } catch (error) {
        console.error('Error fetching weekly tip:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTip();
  }, []);

  if (loading) {
    return (
      <Card variant="muted" padding="md" className="mb-6">
        <div className="flex items-start gap-3">
          <div className="skeleton-shimmer w-8 h-8 rounded-lg" />
          <div className="flex-1 space-y-2">
            <div className="skeleton h-4 w-1/3" />
            <div className="skeleton h-3 w-full" />
            <div className="skeleton h-3 w-2/3" />
          </div>
        </div>
      </Card>
    );
  }

  if (!tip) {
    return null; // Don't show card if no tip available
  }

  return (
    <Card variant="transparent" padding="md" className="mb-6 border-2 border-border shadow-md">
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 w-8 h-8 bg-warning-100 rounded-lg flex items-center justify-center">
          <FaLightbulb className="w-4 h-4 text-warning-600" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-semibold text-text-primary">
              {tip.title}
            </h3>
            {tip.source && (
              <span className="text-xs text-text-muted flex-shrink-0">
                · {tip.source}
              </span>
            )}
          </div>

          <p className="text-sm text-text-secondary leading-relaxed">
            {tip.content}
          </p>

          {tip.learnMoreUrl && (
            <a
              href={tip.learnMoreUrl}
              className="text-xs text-primary-600 hover:text-primary-700 hover:underline mt-2 inline-block transition-colors"
            >
              Les mer →
            </a>
          )}
        </div>
      </div>
    </Card>
  );
};

export default WeeklyTipsCard;
